/**
 * Typed client for the KalmTide backend (workers/). The session token is an
 * opaque string persisted in storage; every authed call sends it as a Bearer
 * header. Errors surface as ApiError with the server's message + details so
 * the UI can show validator output verbatim.
 */

import type { Preset } from "../../schema/types.ts";

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface AuthResult {
  token: string;
  userId: string;
  email: string;
  plan: string;
}

export interface CloudPresetEntry {
  id: string;
  preset: Preset | null;
  updatedAt: number;
  deletedAt: number | null;
}

export interface GenerateResult {
  preset: Preset;
  cached: boolean;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly details: string[] = [],
  ) {
    super(message);
  }
}

const TOKEN_KEY = "kalmtide.session";
const EMAIL_KEY = "kalmtide.email";
const PLAN_KEY = "kalmtide.plan";

export interface Me {
  email: string;
  plan: string;
  expiryMs: number | null;
}

export class ApiClient {
  constructor(
    private readonly baseUrl: string,
    private readonly storage: StorageLike,
    // Wrapped, not bare `fetch`: storing the function unbound and calling it
    // as this.fetchFn(...) sets `this` to the ApiClient, which browsers
    // reject ("'fetch' called on an object that does not implement Window").
    private readonly fetchFn: typeof fetch = (input, init) => fetch(input, init),
  ) {}

  get token(): string | null {
    return this.storage.getItem(TOKEN_KEY);
  }

  get email(): string | null {
    return this.storage.getItem(EMAIL_KEY);
  }

  get signedIn(): boolean {
    return this.token !== null;
  }

  /** Last known plan ("free"/"cloud") — refreshed by me(). */
  get plan(): string | null {
    return this.storage.getItem(PLAN_KEY);
  }

  signOut(): void {
    this.storage.removeItem(TOKEN_KEY);
    this.storage.removeItem(EMAIL_KEY);
    this.storage.removeItem(PLAN_KEY);
  }

  /** Adopt a session minted by the server-side OAuth redirect flow. */
  setSession(token: string, email: string): void {
    this.storage.setItem(TOKEN_KEY, token);
    this.storage.setItem(EMAIL_KEY, email);
  }

  async authGoogle(idToken: string): Promise<AuthResult> {
    const result = await this.request<AuthResult>("POST", "/auth/google", { idToken }, false);
    this.storage.setItem(TOKEN_KEY, result.token);
    this.storage.setItem(EMAIL_KEY, result.email);
    this.storage.setItem(PLAN_KEY, result.plan);
    return result;
  }

  /** Who am I — refreshes the cached plan (server re-checks the sub lazily). */
  async me(): Promise<Me> {
    const result = await this.request<Me>("GET", "/me");
    this.storage.setItem(PLAN_KEY, result.plan);
    return result;
  }

  /**
   * Permanently delete the account and all server-side data (presets,
   * purchase bindings, usage meters). The server kills the session; we
   * drop the local one too so the UI falls back to signed-out.
   */
  async deleteAccount(): Promise<void> {
    await this.request("DELETE", "/me");
    this.signOut();
  }

  async listPresets(): Promise<CloudPresetEntry[]> {
    const body = await this.request<{ presets: CloudPresetEntry[] }>("GET", "/presets");
    return body.presets;
  }

  async putPreset(preset: Preset): Promise<void> {
    await this.request("PUT", `/presets/${encodeURIComponent(preset.meta.id)}`, preset);
  }

  async deletePreset(id: string): Promise<void> {
    await this.request("DELETE", `/presets/${encodeURIComponent(id)}`);
  }

  async generate(prompt: string): Promise<GenerateResult> {
    return this.request<GenerateResult>("POST", "/generate", { prompt });
  }

  /** Cloud voice synthesis — returns PCM16 WAV bytes (24 kHz mono). */
  async tts(text: string, voice = "warm"): Promise<ArrayBuffer> {
    const headers: Record<string, string> = { "content-type": "application/json" };
    if (this.token) headers["Authorization"] = `Bearer ${this.token}`;
    const res = await this.fetchFn(this.baseUrl + "/tts", {
      method: "POST",
      headers,
      body: JSON.stringify({ text, voice }),
    });
    if (!res.ok) {
      const payload = (await res.json().catch(() => ({}))) as { error?: string; details?: string[] };
      if (res.status === 401) this.signOut();
      throw new ApiError(res.status, payload.error ?? `request failed (${res.status})`, payload.details ?? []);
    }
    return res.arrayBuffer();
  }

  private async request<T = unknown>(
    method: string,
    path: string,
    body?: unknown,
    auth = true,
  ): Promise<T> {
    const headers: Record<string, string> = { "content-type": "application/json" };
    if (auth && this.token) headers["Authorization"] = `Bearer ${this.token}`;
    const res = await this.fetchFn(this.baseUrl + path, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    const payload = (await res.json().catch(() => ({}))) as {
      error?: string;
      details?: string[];
    };
    if (!res.ok) {
      if (res.status === 401 && auth) this.signOut(); // stale session
      throw new ApiError(res.status, payload.error ?? `request failed (${res.status})`, payload.details ?? []);
    }
    return payload as T;
  }
}
